import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import MonacoEditor from "react-monaco-editor";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button, Card, CardBody, CardHeader, CardTitle } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faCheck, faXmark, faRobot, faPaperPlane, faTriangleExclamation, faDownload, faRotateLeft, faFileCode } from "@fortawesome/free-solid-svg-icons";
import { requestPromptLabClaude } from "../../services/RobotAIServices.js";
import ListenButton from "../LessonAudio/ListenButton";
import PaneResizer, { clampSize } from "./PaneResizer.js";
import { LiveStatus, useStatus, useOnChange, refocusIfLost } from "./workspaceA11y.js";
import { buildWebLabSrcDoc } from "./webLabHarness.js";
import { WEB_LAB_FILES, DEFAULT_WEB_LAB_FILE, splitWebLabFiles, joinWebLabFiles, describeWebLabFiles } from "./webLabFiles.js";
import { parseWebLabEdits, buildWebLabEditSystem, WEB_LAB_DEFAULT_SYSTEM } from "./webLabEdits.js";
import { buildAuthoredTests, AUTHORED_SEED_STORE } from "../../starterTemplate/authoredLessons.js";
import "./PromptLabLayout.css";
import "./EditorLayout.css";
import "./WebLabLayout.css";
import { AiFirstUseNotice, AiMistakesNote } from "../AiNotice/AiNotice";

/**
 * CD02 "webLab" layout — where students actually build the web app:
 *
 *   +----------+‖------------------+‖---------------------+
 *   |  Lesson  |‖  Editor (HTML)   |‖  Live preview       |
 *   |          |‖                  |‖+===================+|
 *   |          |‖                  |‖  Checks             ||
 *   |          |‖                  |‖  + Ask Claude       ||
 *   +----------+‖------------------+‖---------------------+
 *
 * Ask Claude WRITES as well as explains: when its reply contains a file's new
 * contents, an Apply button puts it in that tab and an Undo takes it back out. The
 * prompt that makes Claude produce something applicable, and the parser that finds
 * it, are in webLabEdits.js. Applying is always the student's click — the point of a
 * module about reading code is not served by code that arrives unread.
 *
 * Same three-pane skeleton and `.pl-resizer` dividers as EditorLayout, but the
 * console is replaced by a rendered page and a list of checks. One self-contained
 * HTML document per lesson (inline <style>/<script>) — the simplest mental model
 * for a beginner and the same shape as starterTemplate/starterAppHtml.js.
 *
 * `multiFile` lessons put a tab strip over that one document instead: index.html,
 * style.css and script.js, split apart on open and joined back before every run.
 * CD04's Module 3 uses it, because a lesson whose title is "CSS: the rules that
 * decide how it looks" should not be showing CSS wedged inside an HTML file. The
 * split and the reasoning behind doing it here rather than in the scaffold are in
 * webLabFiles.js; everything below the editor pane is indifferent to which shape is
 * on screen, because `editorCode` stays a complete page either way.
 *
 * Running is entirely client-side: no OneCompiler call, so web lessons cost
 * nothing to run and work offline. See webLabHarness.js for the sandbox contract
 * and the localStorage shim it forces.
 */

// Where a lesson's shimmed localStorage is persisted on the real origin. Namespaced
// per lesson so one lesson's saved data can't collide with another's.
const storageKeyFor = (lessonID) => `weblab:${lessonID || "unknown"}`;

// This panel had no cap while it only explained things. Now that it writes code it is
// the main way through the lesson for some students, so it needs one — and unlike the
// hub's cap, which console.warns and silently drops the message, this one disables the
// box and says so. Counted in exchanges, not messages, so the number means what a
// student would think it means.
const ASK_CAP_PER_LESSON = 25;

const readStore = (lessonID) => {
    try {
        const raw = window.localStorage.getItem(storageKeyFor(lessonID));
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch (e) {
        return {};
    }
};

const writeStore = (lessonID, store) => {
    try {
        window.localStorage.setItem(storageKeyFor(lessonID), JSON.stringify(store));
    } catch (e) {
        // Quota or privacy mode — the preview still works for this session.
    }
};

const WebLabLayout = ({
    definition,
    lessonTitle,
    editorCode,
    setEditorCode,
    onTestsPassed,
    allPassed,
    downloadable,
    multiFile,
    askHelpSignal,
    solveSignal,
}) => {
    const { t } = useTranslation();
    const [leftWidth, setLeftWidth] = useState(380);
    const [middleWidth, setMiddleWidth] = useState(480);
    const containerRef = useRef(null);
    const iframeRef = useRef(null);

    const lessonID = definition?.lessonID;

    // Taken from the definition, NOT a live i18n read. The hub's init effect only
    // re-runs on route change, so the scaffold in the editor and the exercise text
    // stay in whatever language they were loaded in; reading i18n live would flip
    // the check descriptions out from under them mid-lesson. The hub's freshness
    // gate regenerates whenever `def.locale` disagrees with the UI language, so by
    // the time a definition reaches here the two already match.
    const locale = definition?.locale || "en";

    // Memoized so handleRun's identity is stable — a fresh [] on every render would
    // rebuild the callback continuously.
    //
    // Authored checks live next to the scaffold the student actually gets
    // (authoredLessons.js) and override the generated ones. The generator writes its
    // starter and its checks together, but the hub replaces that starter with the
    // authored scaffold — so its checks would point at elements that don't exist.
    // Their descriptions are shown to the student, hence the locale.
    const domTests = useMemo(() => {
        const authored = buildAuthoredTests(lessonID, locale);
        if (authored) return authored;
        return Array.isArray(definition?.domTests) ? definition.domTests : [];
    }, [definition, lessonID, locale]);

    const [srcDoc, setSrcDoc] = useState(null);
    const [results, setResults] = useState(null);
    const [runtimeError, setRuntimeError] = useState(null);
    const [hasRun, setHasRun] = useState(false);

    // Identifies the current run. Messages from a previous preview (a slow script
    // still posting after the student pressed Run again) carry a stale nonce and
    // are ignored, so late results can't overwrite fresh ones.
    const nonceRef = useRef(null);
    const storeRef = useRef({});

    // Slim "Ask Claude" helper, mirroring EditorLayout's — but this one can hand back
    // code the student applies to their files.
    const [askOpen, setAskOpen] = useState(false);
    const [askInput, setAskInput] = useState("");
    const [askMessages, setAskMessages] = useState([]);
    const [asking, setAsking] = useState(false);
    const askEndRef = useRef(null);
    const askInputRef = useRef(null);
    const capNoteRef = useRef(null);
    const sendHintId = useId();

    // Which of Claude's proposed edits the student has accepted, keyed by message index
    // + file so one reply that changes two files gets two independent buttons and two
    // independent undos. The value is the snapshot taken BEFORE that write, which is
    // why undo can restore the tab they were looking at as well as the code.
    const [applied, setApplied] = useState({});
    // The Apply or Undo button of each proposed edit, keyed like `applied`, and
    // the key whose button should get focus after the next write.
    const editButtonRefs = useRef({});
    const editFocusKey = useRef(null);

    // ----------------------------------------------------------------------
    // Multi-file lessons (CD04 Module 3). `editorCode` remains the single source
    // of truth for everything downstream — the preview, the checks, the download,
    // Ask Claude, the parent's setCurrentCode — and these three files are a VIEW
    // of it. Edits go files-first and are joined straight back up, so the two can
    // never be separately true.
    // ----------------------------------------------------------------------
    const [files, setFiles] = useState(null);
    const [activeFile, setActiveFile] = useState(DEFAULT_WEB_LAB_FILE);
    const tabsId = useId();
    const tabRefs = useRef({});

    // The split happens ONCE per lesson, on the scaffold. Re-splitting whenever
    // `editorCode` changes would fight the student: it changes on every keystroke,
    // and each pass would overwrite the file they are typing in with the joined
    // document's version of it.
    const splitForRef = useRef(null);
    useEffect(() => {
        if (!multiFile) {
            setFiles(null);
            return;
        }
        // `editorCode` arrives asynchronously (buildAuthoredStarter runs after the
        // lesson definition loads), so seed on the first non-empty value.
        if (!editorCode || splitForRef.current === lessonID) return;
        splitForRef.current = lessonID;
        setFiles(splitWebLabFiles(editorCode));
        setActiveFile(DEFAULT_WEB_LAB_FILE);
    }, [multiFile, editorCode, lessonID]);

    const showFiles = !!(multiFile && files);
    const activeFileMeta = WEB_LAB_FILES.find((f) => f.key === activeFile) || WEB_LAB_FILES[0];

    const handleFileChange = (value) => {
        const next = { ...files, [activeFile]: value };
        setFiles(next);
        setEditorCode(joinWebLabFiles(next));
    };

    useEffect(() => {
        const saved = readStore(lessonID);
        // Seed on first open only, so a student's own edits are never overwritten.
        const seed = AUTHORED_SEED_STORE[lessonID];
        storeRef.current = seed && Object.keys(saved).length === 0 ? { ...seed } : saved;
    }, [lessonID]);

    // Initial split sizes — lesson ~26%, editor and preview share the rest.
    useEffect(() => {
        const setInitial = () => {
            const total = containerRef.current ? containerRef.current.clientWidth : window.innerWidth;
            const left = Math.min(440, Math.max(280, Math.floor(total * 0.26)));
            setLeftWidth(left);
            setMiddleWidth(Math.floor((total - left) / 2));
        };
        setInitial();
        window.addEventListener("resize", setInitial);
        return () => window.removeEventListener("resize", setInitial);
    }, []);

    useEffect(() => {
        if (askEndRef.current) askEndRef.current.scrollIntoView({ behavior: "smooth" });
    }, [askMessages.length, asking]);

    // ----------------------------------------------------------------------
    // Messages from the preview.
    //
    // The frame is sandboxed WITHOUT allow-same-origin, so its origin is "null"
    // and `event.origin` can't be used to authenticate. Match on the frame's own
    // window plus this run's nonce instead.
    // ----------------------------------------------------------------------
    useEffect(() => {
        const onMessage = (event) => {
            const frame = iframeRef.current;
            if (!frame || event.source !== frame.contentWindow) return;

            const data = event.data;
            if (!data || typeof data !== "object") return;
            if (!nonceRef.current || data.nonce !== nonceRef.current) return;

            if (data.type === "weblab:results") {
                const list = Array.isArray(data.results) ? data.results : [];
                setResults(list);
                if (list.length > 0 && list.every((r) => r.passed)) onTestsPassed?.();
                return;
            }

            if (data.type === "weblab:error") {
                setRuntimeError(String(data.message || ""));
                return;
            }

            if (data.type === "weblab:storage") {
                const next = { ...storeRef.current };
                if (data.op === "set") next[data.key] = data.value;
                else if (data.op === "remove") delete next[data.key];
                else if (data.op === "clear") Object.keys(next).forEach((k) => delete next[k]);
                storeRef.current = next;
                writeStore(lessonID, next);
            }
        };

        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, [lessonID, onTestsPassed]);

    // Set when "do it for me" writes Claude's files in, so the run happens on the
    // code that just landed rather than the code that was there when it was clicked.
    const runAfterEditRef = useRef(false);

    // Short spoken summaries: a reply is coming or has come, and how the checks went.
    const [status, announce] = useStatus();

    const handleRun = useCallback(() => {
        const nonce = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        nonceRef.current = nonce;
        setResults(null);
        setRuntimeError(null);
        setHasRun(true);
        setSrcDoc(buildWebLabSrcDoc(editorCode || "", domTests, nonce, storeRef.current));
        announce(t("webLab.checking"));
    }, [editorCode, domTests, announce, t]);

    useOnChange(results, (list) => {
        if (!Array.isArray(list)) return;
        announce(t("webLab.statusChecks", { passed: list.filter((r) => r.passed).length, total: list.length }));
    });

    // The other half of applyEditsNow: `editorCode` has now committed, so this is the
    // first moment a run would grade Claude's files and not the student's old ones.
    useEffect(() => {
        if (!runAfterEditRef.current) return;
        runAfterEditRef.current = false;
        handleRun();
    }, [editorCode, handleRun]);

    // Save the student's raw HTML as index.html so they can keep building their app
    // locally with their own Claude. This downloads editorCode ONLY — never the srcDoc,
    // whose injected harness + localStorage shim exist purely for the sandboxed preview
    // and must not ship in the file the student takes home (see webLabHarness.js).
    const handleDownload = useCallback(() => {
        const blob = new Blob([editorCode || ""], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "index.html";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }, [editorCode]);

    // Each divider's limits for a given width, shared by dragging and the keyboard.
    const leftLimits = (width) => ({ min: 260, max: width - middleWidth - 12 - 240 });
    const middleLimits = (width) => ({ min: 260, max: width - leftWidth - 12 - 240 });
    const containerWidth = () => containerRef.current?.clientWidth || window.innerWidth;

    // Drag: lesson ↔ editor.
    const startLeftResize = (e) => {
        e.preventDefault();
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const onMove = (ev) => {
            const { min, max } = leftLimits(rect.width);
            setLeftWidth(clampSize(ev.clientX - rect.left, min, max));
        };
        const stop = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", stop);
            document.body.classList.remove("is-resizing-col");
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", stop);
        document.body.classList.add("is-resizing-col");
    };

    // Drag: editor ↔ preview.
    const startMiddleResize = (e) => {
        e.preventDefault();
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const onMove = (ev) => {
            const { min, max } = middleLimits(rect.width);
            setMiddleWidth(clampSize(ev.clientX - rect.left - leftWidth - 6, min, max));
        };
        const stop = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", stop);
            document.body.classList.remove("is-resizing-col");
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", stop);
        document.body.classList.add("is-resizing-col");
    };

    const asksUsed = askMessages.filter((m) => m.role === "user").length;
    const askCapReached = asksUsed >= ASK_CAP_PER_LESSON;

    // `overrideQuestion` is for the navbar's "Stuck?" panel, which asks on the
    // student's behalf; `autoApply` is what makes "do it for me" different from a
    // normal ask — Claude's files are written straight into the editor instead of
    // waiting behind an Apply button. Applied that way they get no undo snapshot
    // (there is no message row to hang it off), so the student's route back is the
    // editor itself.
    const handleAsk = async (overrideQuestion, { autoApply = false } = {}) => {
        const question = (typeof overrideQuestion === "string" ? overrideQuestion : askInput).trim();
        if (!question || asking || askCapReached) return;

        const failing = (results || []).filter((r) => !r.passed).map((r) => `- ${r.description}`).join("\n");

        // Seed Claude with the code and — the useful part for a web lesson — which
        // checks are still failing, plus any runtime error the page threw. The
        // exercise goes in too: without it Claude answered from the checks alone and
        // could contradict the instructions the student is reading beside it.
        // On a multi-file lesson Claude gets the three files under their own names.
        // Handing it the joined document instead would answer "which file does this
        // go in?" — the question these lessons are actually about — with a page that
        // has no files in it.
        const page = showFiles
            ? `Here are my files:\n\n${describeWebLabFiles(files)}`
            : `Here is my page:\n\n\`\`\`html\n${editorCode || "(empty)"}\n\`\`\``;

        const contextual =
            (definition?.exercisePrompt ? `The exercise I was given:\n${definition.exercisePrompt}\n\n` : "") +
            `${page}\n\n` +
            (failing ? `Checks still failing:\n${failing}\n\n` : "") +
            (runtimeError ? `Error on the page:\n${runtimeError}\n\n` : "") +
            `My question: ${question}`;

        // Claude is sent the contextual blob but the student sees their own question:
        // the thread is a conversation, not a transcript of what was posted.
        const sendMessages = [...askMessages.map(({ role, content }) => ({ role, content })), { role: "user", content: contextual }];
        setAskMessages((prev) => [...prev, { role: "user", content: question }]);
        if (typeof overrideQuestion !== "string") setAskInput("");
        setAsking(true);

        try {
            const sys = buildWebLabEditSystem(definition?.systemPromptForClaude || WEB_LAB_DEFAULT_SYSTEM, showFiles);
            const result = await requestPromptLabClaude(sys, sendMessages, definition?.lessonID);
            const text = result?.text || t("promptLab.noResponse");
            // Parsed once, here, rather than on every render: the reply never changes
            // after it lands, and the edits are keyed by message index below.
            const edits = parseWebLabEdits(text, showFiles, showFiles ? files?.html : editorCode);
            setAskMessages((prev) => [...prev, { role: "assistant", content: text, edits }]);
            if (autoApply) applyEditsNow(edits);
        } catch (e) {
            setAskMessages((prev) => [...prev, { role: "assistant", content: t("promptLab.askCouldNotReach") }]);
        } finally {
            setAsking(false);
        }
    };

    // The checks on screen graded code that is no longer in the editor. Leaving a row
    // of green ticks above freshly changed files is the one way this panel could lie.
    const invalidateRun = () => {
        setResults(null);
        setRuntimeError(null);
        setHasRun(false);
        setSrcDoc(null);
    };

    // Write every file Claude sent back at once, then queue a run so the student sees
    // the checks go green rather than a screen of code they now have to press Run on.
    // The run can't happen here: `editorCode` is state the parent owns, so it is a
    // render away — the effect below fires once it lands.
    const applyEditsNow = (edits) => {
        if (!edits?.length) return;

        if (showFiles) {
            const next = { ...files };
            edits.forEach((edit) => {
                next[edit.fileKey] = edit.code;
            });
            setFiles(next);
            setEditorCode(joinWebLabFiles(next));
            setActiveFile(edits[0].fileKey);
        } else {
            setEditorCode(edits[edits.length - 1].code);
        }
        invalidateRun();
        runAfterEditRef.current = true;
    };

    const applyEdit = (msgIndex, edit) => {
        const key = `${msgIndex}:${edit.fileKey}`;
        editFocusKey.current = key;
        setApplied((prev) => ({ ...prev, [key]: { files, editorCode, activeFile } }));

        if (showFiles) {
            const next = { ...files, [edit.fileKey]: edit.code };
            setFiles(next);
            setEditorCode(joinWebLabFiles(next));
            setActiveFile(edit.fileKey);
        } else {
            setEditorCode(edit.code);
        }
        invalidateRun();
    };

    const undoEdit = (msgIndex, edit) => {
        const key = `${msgIndex}:${edit.fileKey}`;
        const snapshot = applied[key];
        if (!snapshot) return;
        editFocusKey.current = key;

        if (showFiles) {
            setFiles(snapshot.files);
            setEditorCode(joinWebLabFiles(snapshot.files));
            setActiveFile(snapshot.activeFile);
        } else {
            setEditorCode(snapshot.editorCode);
        }
        setApplied((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
        invalidateRun();
    };

    const handleAskKeyDown = (e) => {
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleAsk();
        }
    };

    // Send keeps focus in the box; the button disables itself once the box is
    // empty, which would otherwise drop focus to <body>.
    const askFromButton = () => {
        handleAsk();
        askInputRef.current?.focus();
    };

    useOnChange(asking, (busy) => {
        if (busy) return announce(t("promptLab.statusReplying"));
        announce(t("promptLab.statusReplied"));
        refocusIfLost(askInputRef.current);
    });

    // Apply and Undo replace each other, so focus moves on to the button that
    // took the pressed one's place.
    useEffect(() => {
        const key = editFocusKey.current;
        if (key === null) return;
        editFocusKey.current = null;
        refocusIfLost(editButtonRefs.current[key]);
    }, [applied]);

    // Reaching the cap disables the box. If it had focus, focus moves to the
    // note that says why, which is then read; otherwise the status line says it.
    useOnChange(askCapReached, (reached) => {
        if (!reached) return;
        const active = document.activeElement;
        if (!active || active === document.body || active === askInputRef.current) capNoteRef.current?.focus();
        else announce(t("webLab.capReached"));
    });

    // Arrow keys, Home and End move between the file tabs and open that file.
    const handleTabKeyDown = (e) => {
        const keys = WEB_LAB_FILES.map((file) => file.key);
        const at = keys.indexOf(activeFile);
        let next = null;
        if (e.key === "ArrowRight") next = keys[(at + 1) % keys.length];
        else if (e.key === "ArrowLeft") next = keys[(at - 1 + keys.length) % keys.length];
        else if (e.key === "Home") next = keys[0];
        else if (e.key === "End") next = keys[keys.length - 1];
        if (next === null) return;
        e.preventDefault();
        setActiveFile(next);
        tabRefs.current[next]?.focus();
    };
    const tabId = (key) => `${tabsId}tab-${key}`;
    const panelId = `${tabsId}panel`;

    // Driven by the navbar "Stuck?" panel. Explaining is a normal ask; solving asks
    // for the finished files and applies them. Both are ignored on the initial 0.
    // Both open the Ask panel first, as EditorLayout and HotspotLayout do, so
    // the answer and the AI notices are on screen when the request goes out.
    useEffect(() => {
        if (!askHelpSignal) return;
        setAskOpen(true);
        handleAsk(t("promptLab.hintAskMessage"));
    }, [askHelpSignal]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!solveSignal) return;
        setAskOpen(true);
        handleAsk(t("promptLab.hintSolveCodeMessage"), { autoApply: true });
    }, [solveSignal]); // eslint-disable-line react-hooks/exhaustive-deps

    const dragTitle = t("promptLab.dragToResize", "Drag to resize");
    const editorLabel = t("promptLab.codeEditor", { lang: showFiles ? activeFileMeta.name : "HTML" });
    const exercisePassed = allPassed || (Array.isArray(results) && results.length > 0 && results.every((r) => r.passed));

    return (
        <div ref={containerRef} className="prompt-lab-root">
            {/* LEFT: Lesson */}
            <div className="prompt-lab-pane prompt-lab-pane--lesson" style={{ width: leftWidth, flex: "0 0 auto" }}>
                <div className="prompt-lab-pane-inner">
                    <Card className="prompt-lab-card">
                        <CardHeader className="prompt-lab-card-header">
                            <CardTitle tag="h5" className="mb-0">{lessonTitle || definition?.title || t("promptLab.lessonFallback")}</CardTitle>
                            <ListenButton text={definition?.lessonMarkdown} lessonID={definition?.lessonID} />
                        </CardHeader>
                        <CardBody className="prompt-lab-lesson-body">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {definition?.lessonMarkdown || ""}
                            </ReactMarkdown>

                            {definition?.exercisePrompt && (
                                <div className={`prompt-lab-exercise${exercisePassed ? " prompt-lab-exercise--passed" : ""}`}>
                                    <div className="prompt-lab-exercise-head">
                                        <strong>{t("promptLab.yourExercise")}</strong>
                                        {exercisePassed && (
                                            <span className="prompt-lab-exercise-badge passed">
                                                <FontAwesomeIcon icon={faCheck} /> {t("promptLab.passed")}
                                            </span>
                                        )}
                                    </div>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {definition.exercisePrompt}
                                    </ReactMarkdown>
                                </div>
                            )}

                        </CardBody>
                    </Card>
                </div>
            </div>

            <PaneResizer
                value={leftWidth}
                {...leftLimits(containerWidth())}
                onResize={setLeftWidth}
                onPointerDown={startLeftResize}
                label={t("promptLab.resizePane", { pane: t("promptLab.lessonFallback") })}
                title={dragTitle}
            />

            {/* MIDDLE: HTML editor */}
            <div className="prompt-lab-pane prompt-lab-pane--chat" style={{ width: middleWidth, flex: "0 0 auto" }}>
                <div className="prompt-lab-pane-inner">
                    <Card className="prompt-lab-card">
                        <CardHeader className="prompt-lab-card-header prompt-lab-editor-header">
                            <span>{showFiles ? t("webLab.editorTitleFiles") : t("webLab.editorTitle")}</span>
                            <span className="editor-layout-actions">
                                <Button onClick={handleRun} size="sm" color="success" disabled={!editorCode}>
                                    <FontAwesomeIcon icon={faPlay} /> &nbsp; {t("webLab.run")}
                                </Button>
                                {downloadable && (
                                    <Button onClick={handleDownload} size="sm" color="secondary" outline disabled={!editorCode} style={{ marginLeft: 8 }}>
                                        <FontAwesomeIcon icon={faDownload} /> &nbsp; {t("webLab.downloadApp")}
                                    </Button>
                                )}
                            </span>
                        </CardHeader>
                        <div className="pl-editor-hint">{t("promptLab.editorKeyboardHint")}</div>

                        {/* One tab per file. Always all three, in the same order, on
                            every lesson of the module — the repetition is what makes
                            "a page is three languages" land, the same way the PART 1 /
                            2 / 3 comments do inside the single-file scaffolds. */}
                        {showFiles && (
                            <div className="web-lab-tabs" role="tablist" aria-label={t("webLab.editorTitleFiles")}>
                                {WEB_LAB_FILES.map((file) => (
                                    <button
                                        key={file.key}
                                        ref={(el) => {
                                            tabRefs.current[file.key] = el;
                                        }}
                                        type="button"
                                        role="tab"
                                        id={tabId(file.key)}
                                        aria-selected={file.key === activeFile}
                                        aria-controls={panelId}
                                        tabIndex={file.key === activeFile ? 0 : -1}
                                        className={`web-lab-tab web-lab-tab--${file.key}${file.key === activeFile ? " is-active" : ""}`}
                                        onClick={() => setActiveFile(file.key)}
                                        onKeyDown={handleTabKeyDown}
                                    >
                                        <span className="web-lab-tab-dot" aria-hidden="true" />
                                        {file.name}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* script.js really is empty on the HTML and CSS lessons —
                            the page has no behaviour yet. Without a word here that
                            reads as a broken tab rather than as an empty file, and
                            the student has no way to tell the two apart. */}
                        {showFiles && !String(files[activeFile] || "").trim() && (
                            <div className="web-lab-file-note">{t("webLab.emptyFile")}</div>
                        )}

                        <CardBody
                            className="prompt-lab-editor-body"
                            {...(showFiles ? { id: panelId, role: "tabpanel", "aria-labelledby": tabId(activeFile) } : {})}
                        >
                            <MonacoEditor
                                language={showFiles ? activeFileMeta.language : "html"}
                                theme="vs-dark"
                                value={(showFiles ? files[activeFile] : editorCode) || ""}
                                onChange={(value) => (showFiles ? handleFileChange(value) : setEditorCode(value))}
                                options={{
                                    selectOnLineNumbers: true,
                                    fontSize: 14,
                                    wordWrap: "on",
                                    minimap: { enabled: false },
                                    scrollBeyondLastLine: false,
                                    automaticLayout: true,
                                    ariaLabel: editorLabel,
                                    accessibilitySupport: "auto",
                                }}
                            />
                        </CardBody>
                    </Card>
                </div>
            </div>

            <PaneResizer
                value={middleWidth}
                {...middleLimits(containerWidth())}
                onResize={setMiddleWidth}
                onPointerDown={startMiddleResize}
                label={t("promptLab.resizePane", {
                    pane: showFiles ? t("webLab.editorTitleFiles") : t("webLab.editorTitle"),
                })}
                title={dragTitle}
            />

            {/* RIGHT: live preview + checks + Ask Claude */}
            <div className="prompt-lab-pane prompt-lab-pane--right" style={{ flex: "1 1 auto" }}>
                <div className="web-lab-preview">
                    <Card className="prompt-lab-card">
                        <CardHeader className="prompt-lab-card-header">
                            <span>{t("webLab.previewTitle")}</span>
                            <span className="web-lab-preview-sub">{t("webLab.previewSub")}</span>
                        </CardHeader>
                        <CardBody className="web-lab-preview-body">
                            {srcDoc ? (
                                <iframe
                                    ref={iframeRef}
                                    className="web-lab-frame"
                                    title={t("webLab.previewTitle")}
                                    srcDoc={srcDoc}
                                    // NEVER add allow-same-origin here: combined with
                                    // allow-scripts it dissolves the sandbox and lets
                                    // page code reach the parent's localStorage, where
                                    // Firebase keeps auth tokens. See webLabHarness.js.
                                    sandbox="allow-scripts"
                                />
                            ) : (
                                <div className="web-lab-empty">{t("webLab.emptyPreview")}</div>
                            )}
                        </CardBody>
                    </Card>
                </div>

                <div className="web-lab-checks">
                    <Card className="prompt-lab-card">
                        <CardHeader className="prompt-lab-card-header">
                            <span>{t("webLab.checksTitle")}</span>
                            {Array.isArray(results) && results.length > 0 && (
                                <span className="web-lab-checks-count">
                                    {results.filter((r) => r.passed).length}/{results.length}
                                </span>
                            )}
                        </CardHeader>
                        <CardBody className="web-lab-checks-body">
                            {runtimeError && (
                                <div className="web-lab-runtime-error">
                                    <FontAwesomeIcon icon={faTriangleExclamation} /> {runtimeError}
                                </div>
                            )}

                            {!hasRun && <div className="web-lab-empty-checks">{t("webLab.pressRun")}</div>}

                            {hasRun && !results && <div className="web-lab-empty-checks">{t("webLab.checking")}</div>}

                            {Array.isArray(results) && results.map((r, i) => (
                                <div key={i} className={`web-lab-check${r.passed ? " is-pass" : " is-fail"}`}>
                                    <FontAwesomeIcon icon={r.passed ? faCheck : faXmark} />
                                    <span>{r.description}</span>
                                </div>
                            ))}
                        </CardBody>
                    </Card>
                </div>

                {/* Slim "Ask Claude" hook — same pattern as EditorLayout, plus the
                    Apply cards that make it able to change the student's files. */}
                <div className="editor-ask-claude">
                    {askOpen && (
                        <div className="editor-ask-claude-thread">
                            {/* A log, so new messages are read out; the status line
                                covers "Thinking…". */}
                            <div className="editor-ask-claude-log" role="log" aria-live="polite" aria-relevant="additions">
                                {askMessages.map((m, i) => (
                                    <div key={i} className={`prompt-lab-msg prompt-lab-msg--${m.role}`}>
                                        <div className="prompt-lab-msg-role">{m.role === "user" ? t("promptLab.you") : "Claude"}</div>
                                        <div className="prompt-lab-msg-content">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content || ""}</ReactMarkdown>
                                        </div>

                                        {/* One card per file Claude wants to change. The code
                                            is shown again here, deliberately: it is already
                                            above inside the reply, but a button that writes to
                                            a file has to say what it is about to write right
                                            next to itself. */}
                                        {(m.edits || []).map((edit) => {
                                            const key = `${i}:${edit.fileKey}`;
                                            const isApplied = !!applied[key];
                                            return (
                                                <div key={edit.fileKey} className={`web-lab-edit web-lab-edit--${edit.fileKey}${isApplied ? " is-applied" : ""}`}>
                                                    <div className="web-lab-edit-file">
                                                        <span className="web-lab-tab-dot" aria-hidden="true" />
                                                        {edit.name}
                                                    </div>

                                                    <pre className="web-lab-edit-code"><code>{edit.code}</code></pre>

                                                    {edit.partial && !isApplied && (
                                                        <div className="web-lab-edit-warn">
                                                            <FontAwesomeIcon icon={faTriangleExclamation} /> {t("webLab.editWarnPartial")}
                                                        </div>
                                                    )}

                                                    <div className="web-lab-edit-actions">
                                                        {isApplied ? (
                                                            <>
                                                                <span className="web-lab-edit-done">
                                                                    <FontAwesomeIcon icon={faCheck} /> {t("webLab.applied", { file: edit.name })}
                                                                </span>
                                                                <Button
                                                                    size="sm"
                                                                    color="secondary"
                                                                    outline
                                                                    innerRef={(el) => {
                                                                        editButtonRefs.current[key] = el;
                                                                    }}
                                                                    onClick={() => undoEdit(i, edit)}
                                                                >
                                                                    <FontAwesomeIcon icon={faRotateLeft} /> &nbsp; {t("webLab.undo")}
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <Button
                                                                size="sm"
                                                                color="primary"
                                                                innerRef={(el) => {
                                                                    editButtonRefs.current[key] = el;
                                                                }}
                                                                onClick={() => applyEdit(i, edit)}
                                                            >
                                                                <FontAwesomeIcon icon={faFileCode} /> &nbsp; {t("webLab.applyTo", { file: edit.name })}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                                {asking && (
                                    <div className="prompt-lab-msg prompt-lab-msg--assistant prompt-lab-msg--thinking" aria-hidden="true">
                                        <div className="prompt-lab-msg-content">{t("promptLab.thinking")}</div>
                                    </div>
                                )}
                                <div ref={askEndRef} />
                            </div>

                            {/* Said out loud. The hub's equivalent cap only console.warns,
                                so a student there just watches their message vanish. */}
                            {askCapReached && (
                                <div className="web-lab-ask-cap" ref={capNoteRef} tabIndex={-1}>
                                    {t("webLab.capReached")}
                                </div>
                            )}

                            <AiFirstUseNotice />
                            <div className="editor-ask-claude-input">
                                {/* Read-only, not disabled, while Claude replies, so
                                    keyboard focus stays here. The cap does disable it. */}
                                <textarea
                                    ref={askInputRef}
                                    value={askInput}
                                    onChange={(e) => setAskInput(e.target.value)}
                                    onKeyDown={handleAskKeyDown}
                                    placeholder={t("webLab.askPlaceholder")}
                                    rows={2}
                                    aria-label={t("promptLab.messageLabel")}
                                    aria-describedby={sendHintId}
                                    readOnly={asking}
                                    aria-busy={asking}
                                    disabled={askCapReached}
                                />
                                <Button
                                    size="sm"
                                    color="primary"
                                    onClick={askFromButton}
                                    disabled={asking || askCapReached || !askInput.trim()}
                                    aria-label={t("promptLab.send")}
                                    title={t("promptLab.send")}
                                >
                                    <FontAwesomeIcon icon={faPaperPlane} />
                                </Button>
                            </div>
                            <div id={sendHintId} className="pl-chat-hint">{t("promptLab.sendShortcutHint")}</div>
                            <AiMistakesNote />
                        </div>
                    )}
                    <button type="button" className="editor-ask-claude-toggle" onClick={() => setAskOpen((o) => !o)} aria-expanded={askOpen}>
                        <FontAwesomeIcon icon={faRobot} /> &nbsp;
                        {askOpen ? t("promptLab.hideClaude") : t("webLab.askToggle")}
                    </button>
                </div>
            </div>

            <LiveStatus message={status} />
        </div>
    );
};

export default WebLabLayout;
